import { useCallback } from 'react';

import {
  useChangeAttendanceMutation,
  ChangeAttendanceMutationVariables,
} from 'core/graphql';
import { useParseServerErrors } from 'services/ErrorsService';

type UpdateAttendanceType = [
  (agreementInput: ChangeAttendanceMutationVariables) => void,
  {
    success: boolean;
    loading: boolean;
  },
];

/**
 * Правка отметки посещаемости из сетки журнала.
 *
 * ТЗ 0026: ручной `cache.modify` убран. Мутация всегда возвращала
 * `AttendanceType` — фронт его просто не запрашивал и вместо этого писал в
 * кэш сам, а при неуспехе ИНВЕРТИРОВАЛ значение обратно. Инверсия — не
 * откат: она угадывает прежнее состояние, и на строке, которая была «не
 * отмечена» (null), давала ложное «отсутствовал». С приходом `source` цена
 * такой догадки выросла бы ещё: локально выставленное значение не имеет
 * источника вовсе.
 *
 * Теперь сущность выбирается в самом документе, Apollo нормализует её по id и
 * сам откатывает оптимистику при ошибке.
 */
export const useUpdateAttendance = (): UpdateAttendanceType => {
  const [updateAttendance, { data, loading }] = useChangeAttendanceMutation({
    onError: (err) => err,
  });

  useParseServerErrors(data?.updateAttendance?.customErrors);

  const changeAttendance = useCallback(
    (variables: ChangeAttendanceMutationVariables) => {
      // `attendanceId` объявлен `Int!`, а приходит он из `AttendanceType.id`
      // (`graphene.ID` → строка). graphene 3 после ТЗ 0021 строку в `Int` НЕ
      // приводит: запрос отбивается с HTTP 400 ещё на разборе переменных, до
      // резолвера — поэтому в Sentry пусто, а `errorLink` уводил на «500».
      // Приведение здесь — последний рубеж для любого будущего вызывающего:
      // сетка журнала разбирает строки как `any`, и tsc такой промах не ловит.
      const safeVariables = {
        ...variables,
        attendanceId: Number(variables.attendanceId),
      };

      return updateAttendance({
        variables: safeVariables,
        optimisticResponse: {
          updateAttendance: {
            __typename: 'UpdateAttendanceMutation',
            success: true,
            customErrors: null,
            attendance: {
              __typename: 'AttendanceType',
              // Обратно строкой — Apollo нормализует сущность по `id`, а он у
              // `AttendanceType` строковый. Число здесь создало бы вторую
              // запись в кэше, и оптимистика не легла бы на реальную.
              id: String(safeVariables.attendanceId),
              attendance: variables.attendance,
              source: 'manual',
              markedAt: new Date().toISOString(),
              markedByName: null,
            },
          },
        },
      });
    },
    [updateAttendance],
  );

  return [
    changeAttendance,
    {
      success: Boolean(data?.updateAttendance?.success),
      loading,
    },
  ];
};
